import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@disco/accounts/utils/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full font-neue font-medium text-p-button leading-[100%] tracking-[2%] transition-all duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-white text-black hover:bg-gray-100 focus-visible:ring-white/30',
        black:
          'bg-black text-white hover:bg-gray-800 focus-visible:ring-black/30',
        outline:
          'border-2 border-white/30 text-white hover:border-white/60 hover:bg-white/10 focus-visible:ring-white/30',
        outlineBlack:
          'border border-black text-black hover:border-black hover:bg-black hover:text-white focus-visible:ring-black/30',
        ghost: 'text-white hover:bg-white/10 focus-visible:ring-white/30',
        ghostBlack: 'text-black hover:bg-black/10 focus-visible:ring-black/30',
        yellow:
          'bg-disco-yellow text-black hover:bg-disco-yellow focus-visible:ring-disco-yellow/30',
      },
      size: {
        default: 'h-12 px-8 py-4 text-lg',
        sm: 'h-10 px-6 py-3 text-base',
        lg: 'h-14 px-10 py-5 text-xl',
        xl: 'h-16 px-12 py-6 text-2xl',
        icon: 'h-12 w-12',
      },
      rounded: {
        default: 'rounded-full',
        lg: 'rounded-2xl',
        xl: 'rounded-3xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounded: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, rounded, className }))}
          ref={ref}
          {...props}
        >
          <>{children}</>
        </Slot>
      )
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, rounded, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        )}
        {!loading && leftIcon && <span className="mr-2">{leftIcon}</span>}
        {children}
        {!loading && rightIcon && <span className="ml-2">{rightIcon}</span>}
      </button>
    )
  },
)

Button.displayName = 'Button'

export { Button, buttonVariants }
